import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  DatePickerInput,
  Form,
  Loader,
  Modal,
  SelectInput,
  TextAreaInput,
  TimePickerInput,
} from '@thinknet-gmbh/platform-common-ui';
import { AbsenceTypesEnum } from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetAbsenceReasonsByCompany } from 'src/hooks/absence-reasons/get-absence-reasons-by-company.hook';
import { useGetAllEmployeesForList } from 'src/hooks/employees/get-all-employees-for-list.hook';
import { useCreateEmployeeAbsence } from 'src/hooks/employee-absences/create-absence.hook';

const CreateAbsence = ({
  employeeId,
  isOpened,
  onClose,
}: {
  employeeId?: string;
  isOpened: boolean;
  onClose: () => void;
}) => {
  const { t, i18n } = useTranslation(['employee', 'common']);

  const currentCompanyId = useGetCurrentCompanyId();
  const { absenceReasons, loading: absenceReasonsLoading } =
    useGetAbsenceReasonsByCompany(currentCompanyId);
  const { employees, loading: employeesLoading } = useGetAllEmployeesForList();
  const loading = absenceReasonsLoading || employeesLoading;

  const createAbsenceReason = useCreateEmployeeAbsence({ onSuccess: onClose });

  const onFinish = (values: any) => {
    const { hours, date, ...rest } = values;
    const allDay = !hours?.length;

    const payload = {
      ...rest,
      memberId: employeeId,
      allDay,
      from: allDay ? date[0] : hours[0],
      to: allDay ? date[1] : hours[1],
    };

    createAbsenceReason({ variables: { payload } });
  };

  if (loading && isOpened) {
    return <Loader />;
  }

  return (
    <Modal isOpened={isOpened} title={t('employee:labels.createAbsence')} onClose={onClose}>
      <Form layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="absenceReasonId"
          label={t('employee:labels.absenceType')}
          rules={[{ required: true, message: t('employee:validationError.absenceTypeRequired') }]}
        >
          <SelectInput placeholder="Felling ill">
            {absenceReasons.map((absence: any) => (
              <SelectInput.Option key={absence._id} value={absence._id}>
                {absence.name}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name="paymentType"
          label={t('employee:labels.paymentRequest')}
          rules={[
            { required: true, message: t('employee:validationError.paymentRequestRequired') },
          ]}
        >
          <SelectInput placeholder={t('employee:labels.paid')}>
            <SelectInput.Option value={AbsenceTypesEnum.PAID}>
              {t('employee:labels.paid')}
            </SelectInput.Option>
            <SelectInput.Option value={AbsenceTypesEnum.UNPAID}>
              {t('employee:labels.unpaid')}
            </SelectInput.Option>
          </SelectInput>
        </Form.Item>
        <Form.Item
          name="date"
          label={t('employee:labels.date')}
          rules={[{ required: true, message: t('employee:validationError.dateRequired') }]}
        >
          <DatePickerInput.RangePicker lang={i18n.language} format="DD.MM.YYYY" />
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const value = getFieldValue('date');

            if (value) {
              const [dateFrom, dateTo] = [
                new Date(value[0]).toLocaleDateString(),
                new Date(value[1]).toLocaleDateString(),
              ];

              return (
                dateFrom === dateTo && (
                  <Form.Item name="hours" label={t('employee:labels.hours')}>
                    <TimePickerInput.RangePicker format="HH:mm" />
                  </Form.Item>
                )
              );
            }
          }}
        </Form.Item>
        <Form.Item name="details" label={t('employee:labels.details')}>
          <TextAreaInput
            showCount
            maxLength={200}
            placeholder={t('employee:labels.writeDetails')}
          />
        </Form.Item>
        <Form.Item
          name="approverMemberId"
          label={t('employee:labels.approver')}
          rules={[{ required: true, message: t('employee:validationError.approverRequired') }]}
        >
          <SelectInput showArrow={false} placeholder={t('employee:labels.chooseAbsenceApprover')}>
            {employees.map((employee: any) => (
              <SelectInput.Option key={employee._id} value={employee._id}>
                {employee.profile.displayName}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <ButtonsWrapper>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};

export default CreateAbsence;
